import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { ContentStateValue, ContentStateData } from '../engine/types';
import { StateManager, getDefaultStateData } from '../engine/state';

export interface UseContentStateOptions<TAnswer = unknown> {
  contentId: string;
  initialState?: Partial<ContentStateData<TAnswer>>;
  onStateChange?: (state: ContentStateData<TAnswer>) => void;
  onAnswerChange?: (answer: TAnswer | null) => void;
}

export interface UseContentStateReturn<TAnswer = unknown> {
  // State data
  state: ContentStateData<TAnswer>;
  stateValue: ContentStateValue;
  answer: TAnswer | null;
  
  // State flags
  isInteractive: boolean;
  hasAnswer: boolean;
  showFeedback: boolean;
  showHint: boolean;
  showExplanation: boolean;
  isLocked: boolean;
  isCorrect: boolean | null;
  
  // State actions
  setAnswer: (answer: TAnswer | null) => void;
  setStateValue: (value: ContentStateValue) => void;
  setResult: (isCorrect: boolean, score: number, max: number, feedback?: string) => void;
  setShowFeedback: (show: boolean) => void;
  setShowHint: (show: boolean) => void;
  setShowExplanation: (show: boolean) => void;
  setLocked: (locked: boolean) => void;
  
  // Lifecycle actions
  activate: () => void;
  submit: () => void;
  complete: () => void;
  review: () => void;
  reset: () => void;
  resetForRetry: () => void;
  
  // Manager access
  manager: StateManager<TAnswer>;
}

/**
 * Hook for managing content state
 */
export function useContentState<TAnswer = unknown>(
  options: UseContentStateOptions<TAnswer>
): UseContentStateReturn<TAnswer> {
  const { contentId, initialState, onStateChange, onAnswerChange } = options;

  // Create manager ref to persist across renders
  const managerRef = useRef<StateManager<TAnswer>>();
  if (!managerRef.current) {
    managerRef.current = new StateManager<TAnswer>(contentId, initialState);
  }
  const manager = managerRef.current;

  // State
  const [state, setState] = useState<ContentStateData<TAnswer>>(
    () => manager.getState()
  );

  // Subscribe to manager events
  useEffect(() => {
    const unsubscribe = manager.onAny(() => {
      const newState = manager.getState();
      setState(newState);
      onStateChange?.(newState);
    });

    // Catch anything emitted before this listener existed (child effects run first).
    setState((prev) => {
      const cur = manager.getState();
      return cur.selectedAnswer === prev.selectedAnswer && cur.state === prev.state ? prev : cur;
    });

    return () => {
      unsubscribe();
    };
  }, [manager, onStateChange]);

  // Actions
  const setAnswer = useCallback((answer: TAnswer | null) => {
    manager.setAnswer(answer);
    // Sync React state directly: a child's mount effect can call this before the
    // parent's onAny listener (below) is attached, which would drop the update.
    setState(manager.getState());
    onAnswerChange?.(answer);
  }, [manager, onAnswerChange]);

  const setStateValue = useCallback((value: ContentStateValue) => {
    manager.setStateValue(value);
  }, [manager]);

  const setResult = useCallback((
    isCorrect: boolean, 
    score: number, 
    max: number, 
    feedback?: string
  ) => {
    manager.setResult(isCorrect, score, max, feedback);
  }, [manager]);

  const setShowFeedback = useCallback((show: boolean) => {
    manager.setShowFeedback(show);
    setState(manager.getState());
  }, [manager]);

  const setShowHint = useCallback((show: boolean) => {
    manager.setShowHint(show);
    setState(manager.getState());
  }, [manager]);

  const setShowExplanation = useCallback((show: boolean) => {
    manager.setShowExplanation(show);
    setState(manager.getState());
  }, [manager]);

  const setLocked = useCallback((locked: boolean) => {
    manager.setLocked(locked);
    setState(manager.getState());
  }, [manager]);

  const activate = useCallback(() => {
    manager.activate();
  }, [manager]);

  const submit = useCallback(() => {
    manager.submit();
  }, [manager]);

  const complete = useCallback(() => {
    manager.complete();
  }, [manager]);

  const review = useCallback(() => {
    manager.review();
  }, [manager]);

  const reset = useCallback(() => {
    manager.reset();
  }, [manager]);

  const resetForRetry = useCallback(() => {
    manager.resetForRetry();
    setState(manager.getState());
  }, [manager]);

  // Cleanup
  useEffect(() => {
    return () => {
      manager.destroy();
    };
  }, [manager]);

  return {
    // State data
    state,
    stateValue: state.state,
    answer: state.selectedAnswer,
    
    // State flags
    isInteractive: manager.isInteractive(),
    hasAnswer: manager.hasAnswer(),
    showFeedback: state.showFeedback,
    showHint: state.showHint,
    showExplanation: state.showExplanation,
    isLocked: state.isLocked,
    isCorrect: state.isCorrect,
    
    // State actions
    setAnswer,
    setStateValue,
    setResult,
    setShowFeedback,
    setShowHint,
    setShowExplanation,
    setLocked,
    
    // Lifecycle actions
    activate,
    submit,
    complete,
    review,
    reset,
    resetForRetry,
    
    // Manager access
    manager,
  };
}
